import React from "react";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import Logo from "shared/Logo/Logo";
import { Disclosure } from "@headlessui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { NavItemType } from "./NavigationItem";
import { NAVIGATION_DEMO_2 } from "data/navigation";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import SocialsList from "shared/SocialsList/SocialsList";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import SwitchDarkMode from "shared/SwitchDarkMode/SwitchDarkMode";

export interface NavMobileProps {
  data?: NavItemType[];
  onClickClose?: () => void;
}

interface NavItemButtonProps {
  href?: string;
  hasChildren: boolean;
  itemClass?: string;
  onClickClose?: () => void;
  children?: React.ReactNode;
}

const NavItemButton: React.FC<NavItemButtonProps> = ({
  href,
  hasChildren,
  itemClass = "",
  onClickClose,
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = href ? location.pathname === href : false;

  const className = `flex text-sm rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 mt-0.5 pr-4 ${itemClass} ${
    isActive ? "text-secondary" : ""
  }`;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (hasChildren) return;

    if (href) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        navigate(href);
      }, 400);
    }

    onClickClose && onClickClose();
    // onClickClose && onClickClose();
  };

  return (
    <button type="button" className={className} onClick={handleClick}>
      <span className={`py-2.5 ${!hasChildren ? "block w-full" : ""}`}>
        {children}
      </span>
    </button>
  );
};

const NavMobile: React.FC<NavMobileProps> = ({
  data = NAVIGATION_DEMO_2,
  onClickClose,
}) => {
  const _renderMenuChild = (
    item: NavItemType,
    itemClass = " pl-3 text-neutral-900 dark:text-neutral-200 font-medium "
  ) => {
    return (
      <ul className="nav-mobile-sub-menu pl-6 pb-1 text-base">
        {item.children?.map((i, index) => (
          <Disclosure key={i.href + index} as="li">
            {/** Use navigate instead of NavLink for routing */}
            {/* eslint-disable-next-line @typescript-eslint/no-unused-vars */}
            <NavItemButton
              href={i.href}
              hasChildren={Boolean(i.children)}
              itemClass={itemClass}
              onClickClose={onClickClose}
            >
              {i.name}
            </NavItemButton>
            {i.children && (
              <span
                className="flex items-center flex-grow"
                onClick={(e) => e.preventDefault()}
              >
                <Disclosure.Button
                  as="span"
                  className="flex justify-end flex-grow"
                >
                  <ChevronDownIcon
                    className="ml-2 h-4 w-4 text-slate-500"
                    aria-hidden="true"
                  />
                </Disclosure.Button>
              </span>
            )}
            {i.children && (
              <Disclosure.Panel>
                {_renderMenuChild(
                  i,
                  "pl-3 text-slate-600 dark:text-slate-400 "
                )}
              </Disclosure.Panel>
            )}
          </Disclosure>
        ))}
      </ul>
    );
  };

  const _renderItem = (item: NavItemType, index: number) => {
    return (
      <Disclosure
        key={index}
        as="li"
        className="text-slate-900 dark:text-white"
      >
        <div className="flex w-full items-center py-2.5 px-4 font-medium uppercase tracking-wide text-sm hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">
          <NavItemButton
            href={item.href}
            hasChildren={Boolean(item.children)}
            itemClass={""}
            onClickClose={onClickClose}
          >
            {item.name}
          </NavItemButton>
          {item.children && (
            <span
              className="block flex-grow"
              onClick={(e) => e.preventDefault()}
            >
              <Disclosure.Button
                as="span"
                className="flex justify-end flex-grow"
              >
                <ChevronDownIcon
                  className="ml-2 h-4 w-4 text-neutral-500"
                  aria-hidden="true"
                />
              </Disclosure.Button>
            </span>
          )}
        </div>
        {item.children && (
          <Disclosure.Panel>{_renderMenuChild(item)}</Disclosure.Panel>
        )}
      </Disclosure>
    );
  };

  const renderMagnifyingGlassIcon = () => {
    return (
      <svg
        width={22}
        height={22}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11.5 21C16.7467 21 21 16.7467 21 11.5C21 6.25329 16.7467 2 11.5 2C6.25329 2 2 6.25329 2 11.5C2 16.7467 6.25329 21 11.5 21Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M22 22L20 20"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  const renderSearchForm = () => {
    return (
      <form
        action=""
        method="POST"
        className="flex-1 text-slate-900 dark:text-slate-200"
      >
        <div className="bg-slate-50 dark:bg-slate-800 flex items-center space-x-1 py-2 px-4 rounded-xl h-full">
          {renderMagnifyingGlassIcon()}
          <input
            type="search"
            placeholder="Type and press enter"
            className="border-none bg-transparent focus:outline-none focus:ring-0 w-full text-sm "
          />
        </div>
        <input type="submit" hidden value="" />
      </form>
    );
  };

  return (
    <div className="overflow-y-auto w-full h-screen py-2 transition transform shadow-lg ring-1 dark:ring-neutral-700 bg-white dark:bg-neutral-900 divide-y-2 divide-neutral-100 dark:divide-neutral-800">
      <div className="py-6 px-5">
        <div className="flex items-center gap-2">
          <Logo />
          <p className="lg:mx-4 lg:text-3xl font-bold text-sm">
            {" "}
            <span className="text-primary-500">Devi </span>{" "}
            <span className="text-black"> Plastic </span>{" "}
          </p>
        </div>

        <span className="absolute right-2 top-2 p-1">
          <ButtonClose onClick={onClickClose} />
        </span>

        {/* <div className="mt-5">{renderSearchForm()}</div> */}
      </div>
      <ul className="flex flex-col py-6 px-2 space-y-1">
        {data.map(_renderItem)}
      </ul>
      <div className="px-5 flex justify-between items-center pt-5 ">
        <SocialsList itemClass="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full text-xl" />
      </div>
    </div>
  );
};

export default NavMobile;
